import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../lib/content";
import { controlledTypeStyle } from "../lib/typography";

function fontSize(value, fallback, min, max) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
  return `${safe}px`;
}

/**
 * TiltCard — 3D parallax tilt on mouse move.
 * Pure CSS custom-property driven. No deps.
 */
export default function TiltCard({ project, index }) {
  const { content } = useContent();
  const ref = useRef(null);
  const titleSize = fontSize(content?.typography_project_title_size, 30, 20, 44);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const ry = (x - 0.5) * 12; // rotateY
    const rx = (0.5 - y) * 10; // rotateX
    el.style.setProperty("--ry", `${ry}deg`);
    el.style.setProperty("--rx", `${rx}deg`);
    el.style.setProperty("--mx", `${x * 100}%`);
    el.style.setProperty("--my", `${y * 100}%`);
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--ry", `0deg`);
    el.style.setProperty("--rx", `0deg`);
  };

  const idx = String(index + 1).padStart(2, "0");

  return (
    <Link
      to={`/project/${project.slug}`}
      data-testid={`project-card-${project.slug}`}
      className="group block"
    >
      <div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        className="tilt-card relative aspect-[4/5] overflow-hidden bg-ink-700 hairline"
      >
        <div className="tilt-layer absolute inset-0">
          {project.cover_url ? (
            <img
              src={project.cover_url}
              alt={project.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-[1.06]"
              style={{
                transitionDuration: "1400ms",
                transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)",
              }}
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-bone-200/30 font-mono text-xs uppercase tracking-widest">
              No image
            </div>
          )}
        </div>

        {/* Gradient scrim */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/10 to-transparent"
        />

        {/* Shine on hover */}
        <div className="tilt-shine" />

        {/* Index marker */}
        <div className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-bone-50/70">
          № {idx}
        </div>

        {/* Status tag */}
        {project.year && (
          <div className="absolute top-5 right-5 font-mono text-[10px] uppercase tracking-[0.3em] text-bone-50/70">
            {project.year}
          </div>
        )}

        {/* Bottom meta */}
        <div className="absolute bottom-0 inset-x-0 p-6 md:p-7">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-2">
                {project.location || project.section_name || "Project"}
              </div>
              <h3
                className="font-display text-2xl md:text-3xl leading-[0.95] tracking-tightest text-balance"
                style={controlledTypeStyle({
                  fontSize: titleSize,
                  font: content?.typography_project_title_font,
                })}
                data-testid={`project-title-${project.slug}`}
              >
                {project.title}
              </h3>
            </div>
            <div
              className="flex-shrink-0 h-10 w-10 border border-bone-50/30 flex items-center justify-center group-hover:bg-ember group-hover:border-ember transition-all duration-500"
              aria-hidden
            >
              <span className="translate-y-[-1px]">↗</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
